import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/categories")({
  component: CategoriesPage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchCategories,
  useCategoryMutations,
  type CategoryOrdering,
} from "@/hooks/api/inventory/useCatalog";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDebounce } from "@/hooks/use-debounce";
import { CategoryTable } from "@/components/inventory/tables/CategoryTable";
import { CategoryFormSheet } from "@/components/inventory/forms/CategoryFormSheet";
import type { ItemCategory, ItemCategoryPayload } from "@/types/inventory";

function CategoriesPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // FILTERS + ORDERING
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        is_active: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      CategoryOrdering
    >({ name: null, is_active: null, created_at: null, updated_at: null });

  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [statusFilter, setStatusFilter] = useState<
    "true" | "false" | undefined
  >();
  const [parentFilter, setParentFilter] = useState<string | undefined>();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<ItemCategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ItemCategory | null>(null);

  // QUERIES & MUTATIONS
  const categoriesQuery = useFetchCategories({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_active:
      statusFilter === "true"
        ? true
        : statusFilter === "false"
          ? false
          : undefined,
    parent: parentFilter ? parseInt(parentFilter) : undefined,
  });
  const categories = categoriesQuery.data?.results ?? [];
  const count = categoriesQuery.data?.count ?? 0;

  const { createCategory, updateCategory, deleteCategory } =
    useCategoryMutations();

  const isMutating =
    createCategory.isPending ||
    updateCategory.isPending ||
    deleteCategory.isPending;
  const isLoading = categoriesQuery.isPending || isMutating;
  const isFetching = categoriesQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, statusFilter, parentFilter, setPage]);

  const resetFilters = () => {
    setOrdering({
      name: null,
      is_active: null,
      created_at: null,
      updated_at: null,
    });
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setParentFilter(undefined);
    setPage(1);
  };

  const handleSubmit = async (data: ItemCategoryPayload) => {
    if (editing) {
      await updateCategory.mutateAsync({ id: editing.id, payload: data });
      toast.success("Category updated");
    } else {
      await createCategory.mutateAsync(data);
      toast.success("Category created");
    }
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCategory.mutateAsync({ id: deleteTarget.id });
      toast.success("Category deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Item Categories"
          description="Organize items into hierarchical categories"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => {
            setEditing(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Add Category"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search categories..."
            value={searchInput}
            onChange={setSearchInput}
          />
          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
            ]}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
          <SelectFilter
            placeholder="Parent"
            options={categories.map((c) => ({
              label: c.name,
              value: String(c.id),
            }))}
            value={parentFilter}
            onChange={(v) => setParentFilter(v)}
          />
        </FiltersPanel>

        <CategoryTable
          categories={categories}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onView={(c) => {
            setEditing(c);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(c) => {
            setEditing(c);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(c) => setDeleteTarget(c)}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <CategoryFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          editing={editing}
          categories={categories}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />

        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Category"
          description={`Delete category "${deleteTarget?.name}"? Child categories will also be deleted.`}
          itemName={deleteTarget?.name}
          isLoading={isMutating}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
